import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { AppHeader } from '#/components/layout/app-header'
import { Button } from '#/components/ui/button'
import { Input } from '#/components/ui/input'
import { Textarea } from '#/components/ui/textarea'
import { Progress } from '#/components/ui/progress'
import { api, ApiError } from '#/lib/api'
import { purchaseConsulting, type ConsultingPurchase } from '#/lib/api-misc'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/consulting')({ component: Consulting })

function Consulting() {
  const [step, setStep] = useState(1)
  const [a, setA] = useState<Record<string, string>>({})
  const [done, setDone] = useState(false)
  const [leadId, setLeadId] = useState<string | null>(null)
  const [purchase, setPurchase] = useState<ConsultingPurchase | null>(null)
  const [buying, setBuying] = useState(false)
  const set = (k: string, v: string) => setA((s) => ({ ...s, [k]: v }))
  const Opt = ({ k, v, label }: { k: string; v: string; label: string }) => (
    <button className={cn('w-full rounded-2xl border bg-card p-5 text-left text-base transition-colors hover:border-primary/60', a[k] === v && 'border-primary bg-primary/5')} onClick={() => { set(k, v); setStep(step + 1) }}>{label}</button>
  )
  const submit = async () => {
    try {
      const r = await api<{ ok: true; id: string }>('/api/consulting', { method: 'POST', json: { businessStage: a.stage, budgetBand: a.budget, timeline: a.timeline, needs: a.needs, contact: { name: a.name, email: a.email, whatsapp: a.whatsapp } } })
      setLeadId(r.id)
      setDone(true)
    } catch (e) { toast.error(e instanceof ApiError ? e.message : m.common_error_generic()) }
  }
  // POST /api/consulting/purchase — no payment: marks the lead and returns a mailto confirmation.
  const buy = async () => {
    if (!leadId) return
    setBuying(true)
    try {
      const r = await purchaseConsulting(leadId)
      setPurchase(r)
      window.location.href = r.contact.mailto
    } catch (e) { toast.error(e instanceof ApiError ? e.message : m.common_error_generic()) } finally { setBuying(false) }
  }
  return (
    <>
      <AppHeader />
      <main className="flex-1 overflow-y-auto px-6 py-10">
        <div className="mx-auto w-full max-w-xl space-y-6">
          {done ? (
            <div className="space-y-6 text-center">
              <div className="space-y-2"><h1 className="text-3xl font-bold">{m.consult_done_title()}</h1><p className="text-muted-foreground">{m.consult_done_desc()}</p></div>
              <div className="space-y-3 rounded-2xl border bg-card p-6" data-testid="consult-purchase">
                <h2 className="font-semibold">{m.consult_purchase_title()}</h2>
                <p className="text-sm text-muted-foreground">{m.consult_purchase_desc()}</p>
                {purchase ? (
                  <div className="space-y-2">
                    <p className="text-sm text-status-done">{m.consult_purchase_done({ email: purchase.contact.email })}</p>
                    <Button asChild variant="secondary"><a href={purchase.contact.mailto}>{m.consult_purchase_mail()}</a></Button>
                  </div>
                ) : (
                  <Button onClick={buy} disabled={!leadId || buying}>{m.consult_purchase_btn()}</Button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs text-muted-foreground"><span className="font-medium text-foreground">{m.consult_title()}</span><span>{m.consult_step({ n: step, total: 5 })} · {m.consult_time()}</span></div>
              <Progress value={(step / 5) * 100} className="h-1.5" />
              {step === 1 ? <div className="space-y-3"><h1 className="text-2xl font-bold">{m.consult_q1()}</h1><Opt k="stage" v="running" label={m.consult_q1_a()} /><Opt k="stage" v="validated" label={m.consult_q1_b()} /><Opt k="stage" v="idea" label={m.consult_q1_c()} /></div> : null}
              {step === 2 ? <div className="space-y-3"><h1 className="text-2xl font-bold">{m.consult_q2()}</h1><Opt k="budget" v="low" label={m.consult_q2_a()} /><Opt k="budget" v="mid" label={m.consult_q2_b()} /><Opt k="budget" v="high" label={m.consult_q2_c()} /></div> : null}
              {step === 3 ? <div className="space-y-3"><h1 className="text-2xl font-bold">{m.consult_q3()}</h1><Opt k="timeline" v="30d" label={m.consult_q3_a()} /><Opt k="timeline" v="1-3mo" label={m.consult_q3_b()} /><Opt k="timeline" v="research" label={m.consult_q3_c()} /></div> : null}
              {step === 4 ? <div className="space-y-3"><h1 className="text-2xl font-bold">{m.consult_q4()}</h1><Textarea rows={5} value={a.needs ?? ''} onChange={(e) => set('needs', e.target.value)} placeholder={m.consult_q4_placeholder()} /><div className="flex justify-between"><Button variant="ghost" onClick={() => setStep(3)}>{m.common_back()}</Button><Button disabled={!(a.needs ?? '').trim()} onClick={() => setStep(5)}>{m.common_next()}</Button></div></div> : null}
              {step === 5 ? <div className="space-y-3"><h1 className="text-2xl font-bold">{m.consult_q5()}</h1><Input placeholder={m.consult_q5_name()} value={a.name ?? ''} onChange={(e) => set('name', e.target.value)} /><Input type="email" placeholder={m.consult_q5_email()} value={a.email ?? ''} onChange={(e) => set('email', e.target.value)} /><Input placeholder={m.consult_q5_whatsapp()} value={a.whatsapp ?? ''} onChange={(e) => set('whatsapp', e.target.value)} /><div className="flex justify-between"><Button variant="ghost" onClick={() => setStep(4)}>{m.common_back()}</Button><Button disabled={!a.name || !a.email} onClick={submit}>{m.consult_submit()}</Button></div></div> : null}
            </>
          )}
        </div>
      </main>
    </>
  )
}
